/**
 * WholesalersPage
 * Phase 16: Main page for wholesaler credit management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  RefreshCw,
  Users,
  Filter,
  X,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Eye,
  EyeOff,
  ChevronRight,
  Building2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import {
  getWholesalers,
  getWholesalerStatistics,
  upgradeCustomer,
  updateStatus
} from '../api/wholesalers';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getCustomers } from '../api/customers';

const WholesalersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  // State
  const [wholesalers, setWholesalers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    sortBy: 'customer_name',
    sortOrder: 'ASC'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [upgradeForm, setUpgradeForm] = useState({
    customerId: '',
    creditLimit: '',
    paymentTerms: 30,
    notes: ''
  });
  const [upgrading, setUpgrading] = useState(false);

  // Fetch wholesalers
  const fetchWholesalers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters
      };

      const response = await getWholesalers(params);

      // Backend returns { success: true, data: [...], pagination: {...} }
      setWholesalers(response.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (error) {
      console.error('Error fetching wholesalers:', error);
      toast.error(error.response?.data?.message || 'Failed to load wholesalers');
      setWholesalers([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, filters]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await getWholesalerStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, [isAdmin]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchWholesalers();
    fetchStatistics();
  }, [fetchWholesalers, fetchStatistics]);

  // Load customers for upgrade modal
  const loadCustomersForUpgrade = async () => {
    try {
      const response = await getCustomers({ limit: 100 });
      // Filter out existing wholesalers
      const nonWholesalers = (response.customers || []).filter((c) => !c.is_wholesaler);
      setCustomers(nonWholesalers);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchWholesalers();
  };

  // Handle upgrade
  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (!upgradeForm.customerId || !upgradeForm.creditLimit) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setUpgrading(true);
      await upgradeCustomer({
        customerId: parseInt(upgradeForm.customerId),
        creditLimit: parseFloat(upgradeForm.creditLimit),
        paymentTerms: parseInt(upgradeForm.paymentTerms),
        notes: upgradeForm.notes
      });
      toast.success('Customer upgraded to wholesaler successfully');
      setShowUpgradeModal(false);
      setUpgradeForm({ customerId: '', creditLimit: '', paymentTerms: 30, notes: '' });
      fetchWholesalers();
      fetchStatistics();
    } catch (error) {
      console.error('Error upgrading customer:', error);
      toast.error(error.response?.data?.message || 'Failed to upgrade customer');
    } finally {
      setUpgrading(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (id, newStatus, reason = '') => {
    try {
      await updateStatus(id, newStatus, reason);
      toast.success('Status updated successfully');
      fetchWholesalers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get utilization color
  const getUtilizationColor = (pct) => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary-black flex items-center gap-2">
            <Building2 className="w-7 h-7 text-accent-gold" />
            Wholesaler Credit Management
          </h1>
          <p className="text-primary-slate mt-1">
            Manage wholesaler accounts and credit facilities
          </p>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
          <button
            onClick={() => {
              fetchWholesalers();
              fetchStatistics();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-lightGray text-primary-charcoal rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                setShowUpgradeModal(true);
                loadCustomersForUpgrade();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Upgrade Customer
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards (Admin only) */}
      {isAdmin && statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-slate">Total Wholesalers</p>
                <p className="text-2xl font-bold text-primary-black">
                  {statistics.total_wholesalers || 0}
                </p>
                <p className="text-xs text-green-600 mt-1 font-medium">
                  {statistics.active_wholesalers || 0} active
                </p>
              </div>
              <div className="w-12 h-12 bg-accent-gold/10 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-accent-gold" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-slate">Total Credit Extended</p>
                <p className="text-2xl font-bold text-primary-black">
                  {formatCurrency(statistics.total_credit_extended)}
                </p>
              </div>
              <div className="w-12 h-12 bg-accent-goldLight/10 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-accent-goldDark" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-slate">Outstanding Balance</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(statistics.total_outstanding_balance)}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-slate">Overdue Accounts</p>
                <p className="text-2xl font-bold text-red-600">
                  {statistics.accounts_with_overdue || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-slate" />
            <input
              type="text"
              placeholder="Search by name, phone, or business..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold transition-colors"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-secondary-lightGray transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark transition-colors font-medium"
          >
            Search
          </button>
        </form>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="blocked">Blocked</option>
            </select>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters((prev) => ({ ...prev, sortBy: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="customer_name">Sort by Name</option>
              <option value="credit_limit">Sort by Credit Limit</option>
              <option value="credit_balance">Sort by Balance</option>
              <option value="upgraded_at">Sort by Date Added</option>
            </select>
            <button
              onClick={() => setFilters({ status: '', sortBy: 'customer_name', sortOrder: 'ASC' })}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Wholesalers List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <LoadingSpinner message="Loading wholesalers..." className="py-12" />
        ) : wholesalers.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-primary-black mb-2">No wholesalers found</h3>
            <p className="text-primary-slate mb-4">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : 'Start by upgrading a customer to wholesaler'}
            </p>
            {isAdmin && (
              <button
                onClick={() => {
                  setShowUpgradeModal(true);
                  loadCustomersForUpgrade();
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                Upgrade Customer
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit Limit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance / Available
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {wholesalers.map((w) => (
                  <tr
                    key={w.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/wholesalers/${w.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{w.customer_name}</p>
                        <p className="text-sm text-gray-500">{w.customer_phone}</p>
                        {w.business_name && (
                          <p className="text-xs text-gray-400">{w.business_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{formatCurrency(w.credit_limit)}</p>
                    </td>
                    <td className="px-6 py-4">
                      {w.balance_hidden ? (
                        <div className="flex items-center gap-2 text-gray-400">
                          <EyeOff className="w-4 h-4" />
                          <span className="text-sm">Hidden</span>
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium text-orange-600">
                            {formatCurrency(w.credit_balance)}
                          </p>
                          <p className="text-sm text-green-600">
                            {formatCurrency(w.available_credit)} available
                          </p>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {!w.balance_hidden && (
                        <div className="w-full">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">
                              {w.utilization_percentage || 0}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getUtilizationColor(w.utilization_percentage || 0)}`}
                              style={{ width: `${Math.min(w.utilization_percentage || 0, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(w.status)}`}
                      >
                        {w.status}
                      </span>
                      {w.active_alerts_count > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {w.active_alerts_count}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/wholesalers/${w.id}`);
                        }}
                        className="text-accent-gold hover:text-accent-goldDark transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {wholesalers.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}{' '}
              wholesalers
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 border-2 border-accent-gold">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-primary-black">
                Upgrade Customer to Wholesaler
              </h2>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-primary-charcoal transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpgrade} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Select Customer *
                </label>
                <select
                  value={upgradeForm.customerId}
                  onChange={(e) =>
                    setUpgradeForm((prev) => ({ ...prev, customerId: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold transition-colors"
                  required
                >
                  <option value="">Choose a customer...</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name} - {c.phone}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Credit Limit (₦) *
                </label>
                <input
                  type="number"
                  value={upgradeForm.creditLimit}
                  onChange={(e) =>
                    setUpgradeForm((prev) => ({ ...prev, creditLimit: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold transition-colors"
                  placeholder="e.g. 500000"
                  min="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Payment Terms (Days)
                </label>
                <select
                  value={upgradeForm.paymentTerms}
                  onChange={(e) =>
                    setUpgradeForm((prev) => ({ ...prev, paymentTerms: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold transition-colors"
                >
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                  <option value="45">45 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={upgradeForm.notes}
                  onChange={(e) => setUpgradeForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold transition-colors"
                  rows={3}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-primary-charcoal text-primary-charcoal rounded-lg hover:bg-secondary-lightGray transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={upgrading}
                  className="flex-1 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark disabled:opacity-50 transition-colors font-medium"
                >
                  {upgrading ? 'Upgrading...' : 'Upgrade to Wholesaler'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesalersPage;
